import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgentRole, Item } from '@/types/item';
import { Modal } from '@/types/modal';
import Grid, { GridAction, itemText } from '@/components/molecules/section/Grid';
import Container from '@/components/molecules/section/Container';
import ModalView from '@/components/modals/Modal';
import { transformDateString } from '@/utilities/datetime';
import { useActionTracking, useGenerateInvoiceRequestUrl, useItemRequireAuthentication } from '@/hooks/item';
import { LoadingBodyModal } from '@/components/molecules/common/Modal';

const TEST_ID = 'compliance_info';

const View: React.FC<{ item: Item }> = ({ item }) => {
  const { t } = useTranslation();
  const [modal, setModal] = useState<Modal | null>(null);
  const [open, setOpen] = useState(false);

  const mutateTracking = useActionTracking(item.productId);

  const data = useItemRequireAuthentication(item.productId, open);

  const invoiceUrlQuery = useGenerateInvoiceRequestUrl(item.productId, Number(item.sellerId));

  useEffect(() => {
    if (mutateTracking.kind === 'Error') {
      setModal({
        kind: 'Error',
        title: t('item.compliance.title'),
        message: t('item.compliance.tracking_failed'),
        onClose: () => setModal(null),
      });
    }
  }, [mutateTracking.kind]);

  // Requesting Invoice URL
  useEffect(() => {
    if (invoiceUrlQuery.kind === 'Error') {
      setModal({
        kind: 'Error',
        title: t('item.compliance.title'),
        message: invoiceUrlQuery.error.message || t('item.compliance.no_upload_requested'),
        onClose: () => setModal(null),
      });
      invoiceUrlQuery.remove();
      return;
    }

    if (invoiceUrlQuery.kind !== 'Loaded') return;

    if (invoiceUrlQuery.data.link) {
      window.open(invoiceUrlQuery.data.link, '_blank');
      invoiceUrlQuery.remove();
    }
  }, [invoiceUrlQuery]);

  const isRequestingInvoiceUrl = invoiceUrlQuery.kind === 'Called' && invoiceUrlQuery.fetching;

  const actions: GridAction[] = [
    {
      kind: 'Button',
      label: t('item.compliance.actions.return_button'),
      onClick: () => setModal({ kind: 'ItemReturnFromWarehouse', item, onClose: () => setModal(null) }),
      disabled: !(item.inStock === true),
    },
    {
      kind: 'Button',
      label: t('item.compliance.actions.upload_auth_document'),
      onClick: async () => {
        if (!item.sellerAskInfo || item.sellerAskInfo.statusId !== 0) {
          setModal({
            kind: 'Error',
            title: t('item.compliance.title'),
            message: t('item.compliance.no_upload_requested'),
            onClose: () => setModal(null),
          });
          return;
        }
        setModal({ kind: 'ItemAskMoreInfoModal', item, onClose: () => setModal(null) });
      },
      disabled: isRequestingInvoiceUrl,
    },
    {
      kind: 'Button',
      label: t('item.compliance.actions.view_seller_auth_qc_report'),
      onClick: async () => {
        if (item.sellerId) {
          setModal({
            kind: 'AuthQcReport',
            productId: item.productId,
            agentRole: AgentRole.SELLER,
            onClose: () => setModal(null),
          });
          mutateTracking.mutate('view_seller_auth_qc_report');
        }
      },
      disabled: !item.sellerId,
    },
    {
      kind: 'Button',
      label: t('item.compliance.actions.view_buyer_auth_qc_report'),
      onClick: async () => {
        if (item.buyerId) {
          setModal({
            kind: 'AuthQcReport',
            productId: item.productId,
            agentRole: AgentRole.BUYER,
            onClose: () => setModal(null),
          });
          mutateTracking.mutate('view_buyer_auth_qc_report');
        }
      },
      disabled: !item.buyerId,
    },
  ];

  const getAuthDocumentLabel = () => {
    if (!item.sellerAskInfo) return '-';
    switch (item.sellerAskInfo.statusId) {
      case 0:
        return t('item.compliance.auth_document_required');
      case 1:
        return t('item.compliance.auth_document_provided');
      case 2:
        return t('item.compliance.auth_document_not_provided');
      case 3:
        return t('item.compliance.auth_document_not_answer');
      default:
        return '-';
    }
  };

  if (data.kind === 'Called') {
    return (
      <Container
        title={t('item.compliance.title')}
        testId={TEST_ID}
        actions={actions}
        handler={{ open, setOpen }}
      >
        {open ? <LoadingBodyModal /> : <ModalView modal={modal} />}
      </Container>
    );
  }

  let requireAuthentication = '';
  if (
    data.kind === 'Loaded' &&
    data.data.authenticationRequired !== undefined &&
    data.data.authenticationRequired !== null
  ) {
    requireAuthentication = data.data.authenticationRequired ? t('common.yes') : t('common.no');
  }

  const complianceDate = transformDateString(item.complianceDate, 'DD/MM/YYYY HH:mm:ss');

  const getNegoStatus = (negoStatus: string | null) => {
    if (negoStatus && ['being_negotiated', 'negotiation_accepted', 'negotiation_rejected'].includes(negoStatus)) {
      return t(`item.compliance.nego_status_label.${negoStatus}`);
    }

    return null;
  };

  const items = [
    itemText(t('item.compliance.compliance_date'), complianceDate),
    itemText(t('item.compliance.require_authentication'), requireAuthentication),
    itemText(
      t('item.compliance.compliant'),
      complianceDate && (item.isNonCompliance === '1' ? t('common.no') : t('common.yes')),
    ),
    itemText(t('item.compliance.counterfeit'), item.counterfeit === '1' ? t('common.yes') : t('common.no')),
    itemText(t('item.compliance.item_in_stock'), item.inStock ? t('common.yes') : t('common.no')),
    itemText(t('item.compliance.picking_request_number'), item.pickingRequestNumber),
    itemText(t('item.compliance.picking_request_date'), item.pickingRequestDate),
    itemText(t('item.compliance.nego_status'), getNegoStatus(item.negoStatus), {
      kind: 'Button',
      label: t('common.details'),
      disabled: !item.negoDefectDetails?.length,
      onClick: () => setModal({ kind: 'NegoDetails', item, onClose: () => setModal(null) }),
    }),
    itemText(t('item.compliance.qc_internal_comment'), item.returnInternalComment),
    itemText(t('item.compliance.qc_agent_internal_comment'), item.QCAgentInternalComment),
    itemText(t('item.compliance.auth_document'), getAuthDocumentLabel()),
  ];

  return (
    <>
      <Container
        title={t('item.compliance.title')}
        testId={TEST_ID}
        actions={actions}
        handler={{ open, setOpen }}
      >
        <Grid
          column={5}
          items={items}
        />
      </Container>

      <ModalView modal={modal} />
    </>
  );
};

export default View;
